// Values MUST match the server enum InstantDataStore `ServerClasses/WebAdmin/Base.vb`
// `ParameterType` (and legacy WebAdmin2 `serviceclasses.ts ParamType`). Don't invent
// members — the numeric value is the wire contract. (`Static`/`Normal` were invented
// here and mistranslated `EqualOrLessThen`(1)/`Equal`(0); removed.)
export enum ParamType {
  Equal = 0,
  EqualOrLessThen = 1,
  EqualOrLargerThen = 2,
  LessThen = 3,
  LargerThen = 4,
  Like = 5,
  In = 6,
  Set = 7,
  Specified = 8,
  NotEqual = 9,
  IsNull = 10,
  Unused = 11,
  IsEmpty = 12,
  ClientSide = 13,
  /** Bitwise table-valued membership: (col & value) = value, OR across supplied
   *  values. Server sends each value as an `iValue` row. Use a comma string
   *  (e.g. "1,2,4") as oValue so the server parses it via UnclampIdentityString. */
  InBit = 14,
}

export enum OrderType {
  ASC = 0,
  DESC = 1,
}

export interface InputParam {
  sColumn: string;
  oValue?: unknown;
  Type?: ParamType;
}

/** Extended InputParam with dynamic date instructions for scheduled exports. */
export interface InputParamSE extends InputParam {
  Instructions?: InputParamSEInstruction;
}

export interface InputParamSEInstruction {
  OutputType: "Date";
  Args: DynamicDateOperation[];
}

export interface DynamicDateOperation {
  Operation: "Adjust" | "RoundDown" | "RoundUp";
  Block: "Day" | "Week" | "Month" | "Year";
  Value?: number;
}

export interface SortOrder {
  sColumnName: string;
  OrderType: OrderType;
}

export interface GridSummary {
  sum: unknown[];
  avg: unknown[];
}

export interface GridAttributes {
  header: string[];
  hiddenColumns?: string[];
  hiddenFilters?: string[];
  summaryColumns?: string[];
  updateKeyColumns?: string[];
  pivotColumn?: string;
  pivotGeneratedColumns?: string[];
  pivotColumnDataType?: { Value: string }[];
  functionParameters?: string[];
  rowLineCount?: number;
}

export interface DataSource {
  attributes: GridAttributes;
  matrix: unknown[][];
  binMatrix: string[] | null;
  footerMatrix: string[] | null;
  totalCount: number;
  uniqueCount: number;
  summary: GridSummary;
  dateAsOf: string;
  autoUpdateRate: number;
  iQID: number;
  gridId?: string;
  userQuery?: string;
  /** 2 when the rows came as JSON (`rows`); absent on the legacy binary format. */
  dataVersion?: number;
  /** Version 2: one type per column — int, long, decimal, double, string, bool, datetime, guid,
   * bytes or variant. */
  types?: string[];
  /** Version 2 rows as sent; consumed into `matrix` by parseGridData. */
  rows?: unknown[][] | null;
  footerRows?: unknown[][] | null;
  footerTypes?: string[];
  /** Version 2: the exact text of each decimal cell, by column index then row. Read it through
   * getExactDecimal. */
  decimalText?: Record<number, (string | null)[]>;
  /** Present only when a streamed response failed part-way: the rows it has are correct, but it
   * is not the whole answer. parseGridData reports it (util/incompleteData.ts). */
  incomplete?: GridIncomplete | null;
}

export interface GridIncomplete {
  /** Which grid: its display name. */
  grid?: string | null;
  /** Why, as the server describes errors: plain for everyone, details added for super admins. */
  message: string;
  /** Rows in the response. */
  rowsRead: number;
  /** True when every row arrived and what failed came after them (count, footer totals). */
  rowsComplete: boolean;
}

export interface GridCount {
  totalCount: number;
  uniqueCount: number;
}

export type ExportDataFileType = "CSV" | "XLSX";

export interface ScheduledExport {
  ID?: number;
  Title: string;
  Interval: string;
  Schedule: string;
  AdminUser: number;
  ReportName: string;
  ExportArgs: string;
  AdditionalRecipients: string | null;
}

export enum BuildStates {
  Failed = 0x1000,
  Initializing = 1,
  Downloading = 2,
  Building = 3,
  CreatingFiles = 4,
  Done = 5,
  UnknownError = 6 | 0x1000,
  WaitTimeout = 7 | 0x1000,
  ExceedTotalWeight = 8 | 0x1000,
  /** Stopped by its user (CancelExport). Carries the Failed bit, so check it first. */
  Cancelled = 9 | 0x1000,
  /** Its user asked it to stop; it ends Cancelled within seconds. */
  CancelRequested = 10,
}

export interface ExportInfo {
  DownloadPath: string;
  ExportID: string;
  BuildState: BuildStates;
  FileBuildStatus: number;
  /** Done, but the file holds only the rows read before a database error (FileBuildStatus is then
   * minus that row count). Read through isIncompleteExport. */
  Incomplete?: boolean;
}
